import React, { useCallback, useEffect, useState } from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FadeIn from 'react-fade-in';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Page from 'src/components/Page';
import Breadcrumb from 'src/components/Breadcrumb';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import {
  selectGlobalPaymentsKpi,
  selectGlobalPaymentsList,
  selectGlobalPaymentsListCount,
  selectGlobalPaymentsQueryOptions
} from 'src/store/selectors/globalPaymentSelectors';
import {
  clearGlobalPayments,
  downloadGlobalPaymentsListAction,
  LOAD_GLOBAL_PAYMENTS,
  loadGlobalPayments
} from 'src/store/actions/globalPaymentActions';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import { toMexicoCityInstant } from 'src/utils/momentLocalTimezone';
import useDebounce from 'src/hooks/useDebounce';
import PaymentDetailDrawer from 'src/components/AccountStatement/PaymentDetailDrawer';
import Toolbar from './Toolbar';
import GlobalPaymentTable from './GlobalPaymentTable';
import useKpiStyles from './kpiStyles';

const GlobalPaymentsView = () => {
  const { classes } = useKpiStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const payments = useSelector(selectGlobalPaymentsList);
  const count = useSelector(selectGlobalPaymentsListCount);
  const collectedInRange = useSelector(selectGlobalPaymentsKpi);
  const queryOptions = useSelector(selectGlobalPaymentsQueryOptions);
  const loading = useSelector(isLoadingSelector([LOAD_GLOBAL_PAYMENTS.action]));

  const [search, setSearch] = useState('');
  const [originChannel, setOriginChannel] = useState('');
  const [dateRange, setDateRange] = useState(() => ({
    afterDate: toMexicoCityInstant().startOf('month'),
    beforeDate: toMexicoCityInstant()
  }));
  const [viewingPaymentInvoiceId, setViewingPaymentInvoiceId] = useState(null);

  // dateRange is intentionally omitted from the deps array: this effect only
  // runs on mount to seed the initial (current-month) load, while later
  // dateRange changes are routed through applyFilters/debounceDispatch below.
  useEffect(() => {
    dispatch(
      loadGlobalPayments({
        page: 0,
        afterDate: dateRange.afterDate,
        beforeDate: dateRange.beforeDate
      })
    );
    return () => dispatch(clearGlobalPayments());
  }, [dispatch]);

  const debounceDispatch = useDebounce(
    (options) => dispatch(loadGlobalPayments(options)),
    400
  );

  const applyFilters = useCallback(
    (overrides) => {
      debounceDispatch({
        ...queryOptions,
        page: 0,
        search,
        originChannel: originChannel || undefined,
        afterDate: dateRange.afterDate,
        beforeDate: dateRange.beforeDate,
        ...overrides
      });
    },
    [debounceDispatch, queryOptions, search, originChannel, dateRange]
  );

  const handleSearchChange = useCallback(
    (value) => {
      setSearch(value);
      applyFilters({ search: value });
    },
    [applyFilters]
  );

  const handleOriginChannelChange = useCallback(
    (value) => {
      setOriginChannel(value);
      applyFilters({ originChannel: value || undefined });
    },
    [applyFilters]
  );

  const handleDateRangeChange = useCallback(
    (value) => {
      setDateRange(value);
      applyFilters({
        afterDate: value.afterDate,
        beforeDate: value.beforeDate
      });
    },
    [applyFilters]
  );

  const handleClear = useCallback(() => {
    setSearch('');
    setOriginChannel('');
    setDateRange({});
    // SAVE_GLOBAL_PAYMENTS_OPTIONS merges into existing state.options, so every
    // filter key must be explicitly set to undefined here — omitting a key
    // would leave its previous (stale) value in the store.
    debounceDispatch({
      page: 0,
      search: undefined,
      originChannel: undefined,
      afterDate: undefined,
      beforeDate: undefined
    });
  }, [debounceDispatch]);

  const handleChangePage = useCallback(
    (_, value) => {
      dispatch(loadGlobalPayments({ page: value }));
    },
    [dispatch]
  );

  const handleDownload = useCallback(() => {
    dispatch(downloadGlobalPaymentsListAction());
  }, [dispatch]);

  const handleViewPayment = useCallback((invoiceId) => {
    setViewingPaymentInvoiceId(invoiceId);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setViewingPaymentInvoiceId(null);
  }, []);

  const handleDrawerMutated = useCallback(() => {
    dispatch(loadGlobalPayments(queryOptions));
  }, [dispatch, queryOptions]);

  return (
    <Page className={classes.root} title={t('TRANSACTION.RECEIVED_PAYMENTS_TITLE')}>
      <FadeIn transitionDuration={200}>
        <Container maxWidth={false}>
          <Box className={classes.headerRow}>
            <Box>
              <Breadcrumb
                translationCrumbNumber={[
                  { index: 0, translation: t('TRANSACTION.BREADCRUMB_PARENT') }
                ]}
              />
              <Typography
                style={{ fontWeight: '400', marginTop: 4 }}
                color="primary"
                variant="h1"
              >
                {t('TRANSACTION.RECEIVED_PAYMENTS_TITLE')}
              </Typography>
            </Box>
          </Box>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Box className={classes.cardsRow}>
                <Box className={classes.card}>
                  <Box className={classes.collectedIcon}>
                    <CheckCircleIcon />
                  </Box>
                  <Box>
                    <Typography
                      style={{ marginBottom: 8 }}
                      component="p"
                      variant="body2"
                    >
                      {t('TRANSACTION.PAYMENTS_COLLECTED_IN_RANGE')}
                    </Typography>
                    <Typography color="primary" component="h2" variant="h2">
                      {formatAmountWithDecimals(collectedInRange)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Toolbar
                search={search}
                onSearchChange={handleSearchChange}
                originChannel={originChannel}
                onOriginChannelChange={handleOriginChannelChange}
                dateRange={dateRange}
                onDateRangeChange={handleDateRangeChange}
                onClear={handleClear}
                onDownload={handleDownload}
              />
              <GlobalPaymentTable
                count={count}
                page={queryOptions.page}
                loading={loading}
                handleChangePage={handleChangePage}
                transactions={payments}
                onView={handleViewPayment}
              />
            </Grid>
          </Grid>
        </Container>
      </FadeIn>
      <PaymentDetailDrawer
        open={Boolean(viewingPaymentInvoiceId)}
        invoiceId={viewingPaymentInvoiceId}
        onClose={handleCloseDrawer}
        onMutated={handleDrawerMutated}
      />
    </Page>
  );
};

export default GlobalPaymentsView;
